/**
 * Provider-neutral bounded harness for the native Excel ReAct runner.
 *
 * This module owns only conversation progression and safety limits. Provider
 * request construction, tool registration, workbook verification, checkpoints,
 * and UI integration remain outside the harness so they can evolve without
 * weakening the loop's termination guarantees.
 */

import { isAbortError } from "../shared/utils/abortError";
import {
  EXECUTOR_RUN_TIME_BUDGET_MS,
  MAX_EXECUTOR_ITERATIONS,
  MAX_STAGNANT_EXECUTOR_ITERATIONS,
  createExecutorStagnationBudget,
  normalizeExecutorToolCalls,
} from "./executorLimits";
import type { AgentToolCall, AgentTurn, AgentTurnResult } from "./providers";

const DEFAULT_MAX_PARALLEL_READS = 4;
const DEFAULT_MAX_TOOL_RESULT_CHARS = 256_000;
const DEFAULT_MISSING_COMPLETION_RETRIES = 1;

/** Model-facing control call that explicitly terminates a native run. */
export const COMPLETE_RUN_TOOL_NAME = "complete_run";

/** Stop classifications emitted by the bounded native harness. */
export type NativeReactHarnessStopReason =
  | "iteration_limit"
  | "time_limit"
  | "stagnation"
  | "missing_completion"
  | "provider_error";

/** Immutable request context supplied to one provider turn. */
export interface NativeReactTurnRequest {
  /** Canonical provider-neutral conversation to encode for this turn. */
  turns: readonly AgentTurn[];
  /** One-based model iteration number. */
  iteration: number;
  /** Run cancellation signal, when supplied by the caller. */
  signal?: AbortSignal;
}

/** Immutable execution context supplied with one normalized tool call. */
export interface NativeReactToolExecution {
  /** One-based model iteration that emitted the call. */
  iteration: number;
  /** Run cancellation signal, when supplied by the caller. */
  signal?: AbortSignal;
}

/** Lifecycle events useful to diagnostics and a future taskpane integration. */
export type NativeReactHarnessEvent =
  | { type: "iteration_start"; iteration: number }
  | {
      type: "tool_call_started";
      iteration: number;
      call: AgentToolCall;
    }
  | {
      type: "tool_call_completed";
      iteration: number;
      call: AgentToolCall;
      output: string;
    }
  | {
      type: "stopped";
      iteration: number;
      reason: NativeReactHarnessStopReason;
    };

/** Dependencies and safety budgets for one native ReAct run. */
export interface NativeReactHarnessConfig {
  /** Seed turns, normally metadata-first workbook context plus user request. */
  initialTurns: readonly AgentTurn[];
  /**
   * Execute one provider turn.
   *
   * @param request - Canonical history, iteration number, and cancellation.
   * @returns Provider-normalized assistant text, reasoning, and tool calls.
   */
  requestTurn: (request: NativeReactTurnRequest) => Promise<AgentTurnResult>;
  /**
   * Execute one provider-normalized tool call.
   *
   * @param call - Tool call exactly as normalized by the provider adapter.
   * @param context - Iteration and cancellation context.
   * @returns Model-facing tool output.
   */
  executeTool: (call: AgentToolCall, context: NativeReactToolExecution) => Promise<string>;
  /**
   * Classify calls that mutate workbook or run state.
   *
   * Any stateful call serializes its entire emitted batch so later reads cannot
   * race ahead of an earlier mutation.
   */
  isStatefulToolCall: (call: AgentToolCall) => boolean;
  /**
   * Classify delegated research calls that must run in research-only turns.
   *
   * When omitted, the harness preserves its provider-neutral default and does
   * not impose research/workbook batch isolation.
   */
  isResearchToolCall?: (call: AgentToolCall) => boolean;
  /** Observable run-state fingerprint used to distinguish progress from loops. */
  progressSignature?: () => string;
  /** Hard model-turn cap. Defaults to the current executor cap. */
  maxIterations?: number;
  /** End-to-end wall-clock budget. Defaults to the current executor budget. */
  maxDurationMs?: number;
  /** Consecutive unchanged repeats tolerated before stopping. */
  maxStagnantIterations?: number;
  /** Maximum concurrent calls for an entirely read-only batch. */
  maxParallelReadOnlyToolCalls?: number;
  /** Maximum characters retained from one immediate tool result. */
  maxToolResultChars?: number;
  /** Turns without an explicit completion call tolerated before failing closed. */
  maxMissingCompletionRetries?: number;
  /** Injectable monotonic clock used by focused timeout tests. */
  now?: () => number;
  /** Run cancellation signal. */
  signal?: AbortSignal;
  /**
   * Classify tool failures that make continued execution unsafe.
   *
   * Matching failures propagate to the owning runner instead of becoming
   * recoverable model observations.
   */
  isTerminalToolError?: (error: unknown) => boolean;
  /** Optional structured lifecycle event sink. */
  onEvent?: (event: NativeReactHarnessEvent) => void;
}

/** Terminal result of the bounded provider-neutral native loop. */
export interface NativeReactHarnessResult {
  /** Completed only after a valid explicit completion control call. */
  status: "completed" | "stopped" | "failed";
  /** User-facing terminal text or an honest failure explanation. */
  reply: string;
  /** Bounded-stop classification for stopped and failed outcomes. */
  stopReason?: NativeReactHarnessStopReason;
  /** Number of provider turns attempted. */
  iterations: number;
  /** Canonical replay history retained for diagnostics and integration. */
  turns: AgentTurn[];
}

/**
 * Run a provider-neutral native ReAct conversation under hard safety budgets.
 *
 * @param config - Provider/tool callbacks, initial turns, and bounded budgets.
 * @returns A completed, safely stopped, or failed native-loop result.
 */
export async function runBoundedNativeReactHarness(
  config: NativeReactHarnessConfig
): Promise<NativeReactHarnessResult> {
  const maxIterations = positiveInteger(config.maxIterations, MAX_EXECUTOR_ITERATIONS);
  const maxDurationMs = positiveNumber(config.maxDurationMs, EXECUTOR_RUN_TIME_BUDGET_MS);
  const maxStagnantIterations = positiveInteger(
    config.maxStagnantIterations,
    MAX_STAGNANT_EXECUTOR_ITERATIONS
  );
  const maxParallelReadOnlyToolCalls = positiveInteger(
    config.maxParallelReadOnlyToolCalls,
    DEFAULT_MAX_PARALLEL_READS
  );
  const maxToolResultChars = positiveInteger(
    config.maxToolResultChars,
    DEFAULT_MAX_TOOL_RESULT_CHARS
  );
  const maxMissingCompletionRetries = nonNegativeInteger(
    config.maxMissingCompletionRetries,
    DEFAULT_MISSING_COMPLETION_RETRIES
  );
  const now = config.now ?? Date.now;
  const startedAt = now();
  const deadline = createDeadlineScope(config.signal, maxDurationMs);
  const turns = config.initialTurns.map(cloneTurn);
  const stagnation = createExecutorStagnationBudget(maxStagnantIterations);
  let missingCompletionRetries = 0;
  let iterations = 0;

  try {
    for (let index = 0; index < maxIterations; index += 1) {
      throwIfAborted(config.signal);
      if (now() - startedAt >= maxDurationMs) {
        return stoppedResult(turns, iterations, "time_limit", config.onEvent);
      }

      const iteration = index + 1;
      iterations = iteration;
      config.onEvent?.({ type: "iteration_start", iteration });

      let modelTurn: AgentTurnResult;
      try {
        modelTurn = await deadline.race(
          config.requestTurn({
            turns: turns.map(cloneTurn),
            iteration,
            signal: deadline.signal,
          })
        );
        throwIfAborted(config.signal);
      } catch (error) {
        throwIfAborted(config.signal);
        if (deadline.didExpire || error instanceof DeadlineExceededError) {
          return stoppedResult(turns, iterations, "time_limit", config.onEvent);
        }
        rethrowAbort(error, config.signal);
        const detail = error instanceof Error ? error.message : String(error);
        config.onEvent?.({
          type: "stopped",
          iteration,
          reason: "provider_error",
        });
        return {
          status: "failed",
          reply: `The model turn could not be completed: ${detail}`,
          stopReason: "provider_error",
          iterations,
          turns,
        };
      }

      const toolCalls = modelTurn.toolCalls;
      const completionCalls = toolCalls.filter(
        (call) => call.name === COMPLETE_RUN_TOOL_NAME,
      );
      if (completionCalls.length > 0) {
        if (toolCalls.length !== 1) {
          turns.push({ kind: "model", raw: modelTurn.raw.slice() });
          appendControlErrorResults(
            turns,
            toolCalls,
            `${COMPLETE_RUN_TOOL_NAME} must be the only tool call in its turn. ` +
              "No calls in this batch were executed. Continue the work, then call it alone.",
          );
          continue;
        }

        const completion = validateCompletionCall(
          completionCalls[0],
          modelTurn.text,
        );
        if ("error" in completion) {
          turns.push({ kind: "model", raw: modelTurn.raw.slice() });
          turns.push({
            kind: "tool_result",
            callId: completionCalls[0].callId,
            output: JSON.stringify({
              ok: false,
              code: "invalid_complete_run",
              error: completion.error,
            }),
          });
          continue;
        }

        return {
          status: "completed",
          reply: completion.answer,
          iterations,
          turns,
        };
      }

      if (toolCalls.length === 0) {
        if (missingCompletionRetries >= maxMissingCompletionRetries) {
          config.onEvent?.({
            type: "stopped",
            iteration,
            reason: "missing_completion",
          });
          return {
            status: "failed",
            reply: `The model did not call ${COMPLETE_RUN_TOOL_NAME} to finish the run.`,
            stopReason: "missing_completion",
            iterations,
            turns,
          };
        }
        missingCompletionRetries += 1;
        if (modelTurn.raw.length > 0) {
          turns.push({ kind: "model", raw: modelTurn.raw.slice() });
        }
        turns.push({
          kind: "user",
          content:
            `Your previous turn did not call ${COMPLETE_RUN_TOOL_NAME}, so the workflow is still active. ` +
            `Continue with the necessary tools, or call ${COMPLETE_RUN_TOOL_NAME} alone when the work is done.`,
        });
        continue;
      }
      missingCompletionRetries = 0;

      const progress = config.progressSignature?.() ?? "";
      const normalizedCalls = normalizeExecutorToolCalls(toolCalls);
      if (stagnation.record(normalizedCalls, progress)) {
        return stoppedResult(turns, iterations, "stagnation", config.onEvent);
      }

      turns.push({ kind: "model", raw: modelTurn.raw.slice() });
      const researchClassifications = toolCalls.map(
        config.isResearchToolCall ?? (() => false),
      );
      if (
        researchClassifications.some(Boolean) &&
        researchClassifications.some((isResearch) => !isResearch)
      ) {
        appendMixedResearchBatchErrorResults(
          turns,
          toolCalls,
          iteration,
          config.onEvent,
        );
        continue;
      }
      const batchIsStateful = toolCalls.some(config.isStatefulToolCall);
      let outputs: string[];
      try {
        outputs = batchIsStateful
          ? await executeSequentially(toolCalls, iteration, config, deadline)
          : await executeWithConcurrency(
              toolCalls,
              iteration,
              maxParallelReadOnlyToolCalls,
              config,
              deadline
            );
      } catch (error) {
        throwIfAborted(config.signal);
        if (deadline.didExpire || error instanceof DeadlineExceededError) {
          return stoppedResult(turns, iterations, "time_limit", config.onEvent);
        }
        throw error;
      }

      for (let callIndex = 0; callIndex < toolCalls.length; callIndex += 1) {
        const toolCall = toolCalls[callIndex];
        const output = boundToolOutput(outputs[callIndex], maxToolResultChars);
        turns.push({
          kind: "tool_result",
          callId: toolCall.callId,
          output,
        });
        config.onEvent?.({
          type: "tool_call_completed",
          iteration,
          call: toolCall,
          output,
        });
      }
    }

    return stoppedResult(turns, iterations, "iteration_limit", config.onEvent);
  } finally {
    deadline.dispose();
  }
}

/**
 * Execute a model-emitted batch in strict source order.
 *
 * The harness selects this path whenever any call in the batch is stateful so
 * reads cannot race ahead of workbook or run-state mutations.
 *
 * @param calls - Provider-normalized calls in model-emitted order.
 * @param iteration - One-based model iteration that emitted the batch.
 * @param config - Run-scoped execution callbacks, signal, and event sink.
 * @returns Tool outputs aligned by index with `calls`.
 */
async function executeSequentially(
  calls: readonly AgentToolCall[],
  iteration: number,
  config: NativeReactHarnessConfig,
  deadline: DeadlineScope
): Promise<string[]> {
  const outputs: string[] = [];
  for (const toolCall of calls) {
    if (deadline.didExpire) {
      outputs.push(
        JSON.stringify({
          ok: false,
          skipped: true,
          code: "run_time_limit",
          error:
            "The run deadline expired before this queued tool call started.",
        })
      );
      continue;
    }
    outputs.push(await executeSafely(toolCall, iteration, config, deadline));
  }
  return outputs;
}

/**
 * Execute an entirely read-only batch under a fixed worker limit.
 *
 * @param calls - Provider-normalized read-only calls to execute.
 * @param iteration - One-based model iteration that emitted the batch.
 * @param concurrency - Maximum workers allowed to execute simultaneously.
 * @param config - Run-scoped execution callbacks, signal, and event sink.
 * @returns Tool outputs aligned by index with `calls`, regardless of finish order.
 */
async function executeWithConcurrency(
  calls: readonly AgentToolCall[],
  iteration: number,
  concurrency: number,
  config: NativeReactHarnessConfig,
  deadline: DeadlineScope
): Promise<string[]> {
  const outputs = new Array<string>(calls.length);
  let nextIndex = 0;
  const worker = async (): Promise<void> => {
    while (nextIndex < calls.length) {
      const index = nextIndex;
      nextIndex += 1;
      outputs[index] = await executeSafely(calls[index], iteration, config, deadline);
    }
  };
  await Promise.all(Array.from({ length: Math.min(concurrency, calls.length) }, () => worker()));
  return outputs;
}

/**
 * Execute one tool call without allowing ordinary tool failures to tear down
 * the reasoning loop.
 *
 * Cancellation remains exceptional and propagates immediately. Other failures
 * become structured observations so the model can recover on its next turn.
 *
 * @param toolCall - Provider-normalized call to dispatch.
 * @param iteration - One-based model iteration that emitted the call.
 * @param config - Run-scoped execution callback, signal, and event sink.
 * @returns Successful output or a serialized recoverable error observation.
 * @throws An abort error when the run is cancelled before or during execution.
 */
async function executeSafely(
  toolCall: AgentToolCall,
  iteration: number,
  config: NativeReactHarnessConfig,
  deadline: DeadlineScope
): Promise<string> {
  throwIfAborted(config.signal);
  if (deadline.didExpire) throw new DeadlineExceededError();
  config.onEvent?.({
    type: "tool_call_started",
    iteration,
    call: toolCall,
  });
  try {
    const output = await deadline.race(
      config.executeTool(toolCall, {
        iteration,
        signal: deadline.signal,
      }),
      {
        settleOnDeadline: config.isStatefulToolCall(toolCall),
      }
    );
    throwIfAborted(config.signal);
    return output;
  } catch (error) {
    throwIfAborted(config.signal);
    if (deadline.didExpire || error instanceof DeadlineExceededError) {
      throw error;
    }
    rethrowAbort(error, config.signal);
    if (config.isTerminalToolError?.(error)) throw error;
    return JSON.stringify({
      ok: false,
      error: error instanceof Error ? error.message : String(error),
    });
  }
}

/** Internal marker used to distinguish deadline expiry from user cancellation. */
class DeadlineExceededError extends Error {
  constructor() {
    super("Native ReAct run deadline exceeded");
    this.name = "DeadlineExceededError";
  }
}

/** Run-scoped deadline signal and race lifecycle. */
interface DeadlineScope {
  /** Signal aborted by either external cancellation or deadline expiry. */
  signal: AbortSignal;
  /** Whether the wall-clock deadline, rather than external cancellation, fired. */
  readonly didExpire: boolean;
  /**
   * Race one in-flight operation against cancellation and deadline expiry.
   *
   * @param operation - Provider or tool operation to bound.
   * @param options - Deadline behavior for already-dispatched operations.
   * @returns The operation result when it settles before cancellation.
   * @throws {DeadlineExceededError} When the wall-clock deadline expires.
   * @throws {DOMException} When the caller cancels the run.
   */
  race<T>(
    operation: Promise<T>,
    options?: { settleOnDeadline?: boolean }
  ): Promise<T>;
  /** Release timer and external-signal listeners owned by this scope. */
  dispose(): void;
}

/**
 * Compose caller cancellation with one real wall-clock run deadline.
 *
 * @param externalSignal - Optional caller-owned cancellation signal.
 * @param durationMs - Positive deadline duration in milliseconds.
 * @returns A disposable signal/race scope shared by all in-flight operations.
 */
function createDeadlineScope(
  externalSignal: AbortSignal | undefined,
  durationMs: number
): DeadlineScope {
  const controller = new AbortController();
  let didExpire = false;
  const abortFromExternal = (): void => {
    controller.abort(externalSignal?.reason);
  };
  if (externalSignal?.aborted) abortFromExternal();
  else
    externalSignal?.addEventListener("abort", abortFromExternal, {
      once: true,
    });

  const timer = setTimeout(() => {
    didExpire = true;
    controller.abort(new DeadlineExceededError());
  }, durationMs);

  return {
    signal: controller.signal,
    get didExpire() {
      return didExpire;
    },
    async race<T>(
      operation: Promise<T>,
      options: { settleOnDeadline?: boolean } = {}
    ): Promise<T> {
      if (didExpire) throw new DeadlineExceededError();
      throwIfAborted(externalSignal);

      let removeAbortListeners = (): void => undefined;
      const aborted = new Promise<never>((_resolve, reject) => {
        const rejectForDeadlineOrCancellation = (): void => {
          if (didExpire && options.settleOnDeadline === true) {
            return;
          }
          reject(
            didExpire ? new DeadlineExceededError() : new DOMException("Aborted", "AbortError")
          );
        };
        const rejectForExternalCancellation = (): void => {
          reject(new DOMException("Aborted", "AbortError"));
        };
        if (controller.signal.aborted) {
          rejectForDeadlineOrCancellation();
        } else {
          controller.signal.addEventListener("abort", rejectForDeadlineOrCancellation, {
            once: true,
          });
        }
        if (externalSignal?.aborted) {
          rejectForExternalCancellation();
        } else {
          externalSignal?.addEventListener(
            "abort",
            rejectForExternalCancellation,
            { once: true },
          );
        }
        removeAbortListeners = () => {
          controller.signal.removeEventListener(
            "abort",
            rejectForDeadlineOrCancellation,
          );
          externalSignal?.removeEventListener(
            "abort",
            rejectForExternalCancellation,
          );
        };
      });
      try {
        return await Promise.race([operation, aborted]);
      } finally {
        removeAbortListeners();
      }
    },
    dispose(): void {
      clearTimeout(timer);
      externalSignal?.removeEventListener("abort", abortFromExternal);
    },
  };
}

function validateCompletionCall(
  call: AgentToolCall,
  assistantText: string,
): { answer: string } | { error: string } {
  let value: unknown;
  try {
    value = JSON.parse(call.arguments) as unknown;
  } catch {
    return { error: "Arguments must be valid JSON." };
  }
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return { error: "Arguments must be an object." };
  }
  if (Object.keys(value).length > 0) {
    return { error: `${COMPLETE_RUN_TOOL_NAME} does not accept arguments.` };
  }
  const answer = assistantText.trim();
  if (!answer) {
    return {
      error:
        "Write the final user-facing explanation as assistant text in the same turn, then call complete_run.",
    };
  }
  return { answer };
}

function appendControlErrorResults(
  turns: AgentTurn[],
  calls: readonly AgentToolCall[],
  error: string,
): void {
  for (const call of calls) {
    turns.push({
      kind: "tool_result",
      callId: call.callId,
      output: JSON.stringify({
        ok: false,
        code: "invalid_completion_batch",
        error,
      }),
    });
  }
}

/**
 * Reject one mixed research/non-research batch without dispatching any calls.
 *
 * @param turns - Canonical conversation receiving correlated tool results.
 * @param calls - Model-emitted calls rejected as one atomic batch.
 * @param iteration - One-based model iteration that emitted the batch.
 * @param onEvent - Optional lifecycle observer used by the taskpane timeline.
 * @returns Nothing.
 */
function appendMixedResearchBatchErrorResults(
  turns: AgentTurn[],
  calls: readonly AgentToolCall[],
  iteration: number,
  onEvent?: (event: NativeReactHarnessEvent) => void,
): void {
  for (const call of calls) {
    const output = JSON.stringify({
      ok: false,
      skipped: true,
      retryable: true,
      code: "mixed_research_workbook_batch",
      error:
        "Research tools must be called in a research-only turn. No calls in this batch were executed.",
      instruction:
        "Retry research calls in one turn, then use their results in a later non-research turn.",
    });
    turns.push({
      kind: "tool_result",
      callId: call.callId,
      output,
    });
    onEvent?.({
      type: "tool_call_started",
      iteration,
      call,
    });
    onEvent?.({
      type: "tool_call_completed",
      iteration,
      call,
      output,
    });
  }
}

/**
 * Construct a consistent bounded-stop result and notify diagnostics.
 *
 * @param turns - Canonical conversation accumulated before termination.
 * @param iterations - Number of provider turns attempted.
 * @param reason - Safety budget that terminated the loop.
 * @param onEvent - Optional lifecycle observer.
 * @returns A stopped result with honest user-facing copy.
 */
function stoppedResult(
  turns: AgentTurn[],
  iterations: number,
  reason: Exclude<NativeReactHarnessStopReason, "missing_completion" | "provider_error">,
  onEvent?: (event: NativeReactHarnessEvent) => void
): NativeReactHarnessResult {
  onEvent?.({ type: "stopped", iteration: iterations, reason });
  const reply =
    reason === "iteration_limit"
      ? "The run stopped at its iteration limit before a final response."
      : reason === "time_limit"
        ? "The run stopped at its time limit before a final response."
        : "The run stopped because repeated tool calls made no progress.";
  return {
    status: "stopped",
    reply,
    stopReason: reason,
    iterations,
    turns,
  };
}

/**
 * Bound one immediate tool observation while retaining useful head and tail
 * evidence for model recovery.
 *
 * @param output - Untrusted tool output to place in canonical history.
 * @param maxChars - Maximum characters retained for this observation.
 * @returns Original output when bounded, otherwise a marked head/tail excerpt.
 */
function boundToolOutput(output: string, maxChars: number): string {
  if (output.length <= maxChars) return output;
  const marker = `\n...[truncated output; original length ${output.length}]...\n`;
  if (marker.length >= maxChars) {
    return `[truncated ${output.length}]`.slice(0, maxChars);
  }
  const remaining = maxChars - marker.length;
  const headChars = Math.ceil(remaining * 0.7);
  const tailChars = remaining - headChars;
  return `${output.slice(0, headChars)}${marker}${output.slice(-tailChars)}`;
}

/**
 * Copy a canonical turn before exposing history to provider integrations.
 *
 * @param turn - Canonical user, model, or tool-result turn.
 * @returns A shallow structural copy whose array fields cannot mutate history.
 */
function cloneTurn(turn: AgentTurn): AgentTurn {
  switch (turn.kind) {
    case "user":
      return { kind: "user", content: turn.content };
    case "model":
      return { kind: "model", raw: turn.raw.slice() };
    case "tool_result":
      return {
        kind: "tool_result",
        callId: turn.callId,
        output: turn.output,
      };
  }
}

/**
 * Enforce cooperative run cancellation at harness boundaries.
 *
 * @param signal - Optional run-scoped cancellation signal.
 * @returns Nothing.
 * @throws An abort error when `signal` is already aborted.
 */
function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) {
    throw new DOMException("Aborted", "AbortError");
  }
}

/**
 * Preserve abort failures while leaving ordinary failures to caller policy.
 *
 * @param error - Failure caught from a provider or tool callback.
 * @param signal - Optional run-scoped cancellation signal.
 * @returns Nothing when the failure is unrelated to cancellation.
 * @throws The original abort failure or a synthesized AbortError.
 */
function rethrowAbort(error: unknown, signal?: AbortSignal): void {
  if (isAbortError(error)) throw error;
  throwIfAborted(signal);
}

/**
 * Validate a strictly positive integer budget.
 *
 * @param value - Caller-supplied candidate.
 * @param fallback - Safe default used for absent or invalid candidates.
 * @returns `value` when valid; otherwise `fallback`.
 */
function positiveInteger(value: number | undefined, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) && Number.isInteger(value) && value > 0
    ? value
    : fallback;
}

/**
 * Validate an integer budget that may intentionally be zero.
 *
 * @param value - Caller-supplied candidate.
 * @param fallback - Safe default used for absent or invalid candidates.
 * @returns `value` when valid; otherwise `fallback`.
 */
function nonNegativeInteger(value: number | undefined, fallback: number): number {
  return typeof value === "number" &&
    Number.isFinite(value) &&
    Number.isInteger(value) &&
    value >= 0
    ? value
    : fallback;
}

/**
 * Validate a finite, strictly positive numeric budget.
 *
 * @param value - Caller-supplied candidate.
 * @param fallback - Safe default used for absent or invalid candidates.
 * @returns `value` when valid; otherwise `fallback`.
 */
function positiveNumber(value: number | undefined, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) && value > 0 ? value : fallback;
}
